/**
 * Type-safe shape probes for tRPC error causes.
 *
 * tRPC bubbles `cause: { reason: string, ... }` from the server's
 * `TRPCError({ cause: { … } })` to the client's caught error as
 * `err.data.cause`. The router's `reraise` helpers attach a small
 * structured shape — these probes let callers narrow against it
 * without scattering `as` casts at every catch site.
 *
 * Usage:
 *
 *   try { await mutation.mutateAsync(...) }
 *   catch (err) {
 *     if (isTrpcCauseShape(err, { reason: 'locked-field' })) {
 *       // err.data.cause.field is `string | undefined` after narrow
 *     }
 *   }
 *
 * Adding a new cause shape: extend the conditional union in `TrpcCause`
 * below + add a `reason` literal. The router's reraise should match
 * the same `reason` strings.
 */

/**
 * Discriminated union of every cause-shape any router builds. Keep this
 * in sync with the `cause: { reason: '…', … }` literals in
 * `apps/api/src/routers/*.ts` reraise helpers.
 */
export type TrpcCause =
  | { reason: "locked-field"; field?: "pan" | "cin" | "gstin" }
  | { reason: "storage-disabled" }
  | { reason: "no-changes" }
  | { reason: "company-not-found" }
  | { reason: "user-not-found" }
  | { reason: "logo-key-ownership" }
  | { reason: "avatar-key-ownership" }
  | { reason: "otp-invalid"; attemptsRemaining?: number }
  | { reason: "otp-expired" }
  | { reason: "otp-attempts-exceeded" }
  | { reason: "otp-consumed" }
  | { reason: "otp-not-found" }
  | { reason: "gstin-claimed"; gstin?: string }
  | { reason: "workspace-exists" }
  | { reason: "invalid-prefix"; field?: "prefix" }
  | { reason: "invalid-padding"; field?: "padding" }
  | { reason: "invalid-next-seq"; field?: "nextSeq" }
  | { reason: "next-seq-locked"; field?: "nextSeq" }
  | { reason: "subscription-ended" };

type Reason = TrpcCause["reason"];

/** Shape of an error object whose cause matches a specific reason. */
type CauseFor<R extends Reason> = Extract<TrpcCause, { reason: R }>;

/** The narrowed error shape after a successful probe. */
export type TrpcErrorWithCause<R extends Reason> = {
  data: { cause: CauseFor<R> };
};

/**
 * True when `err` is a tRPC error whose cause matches the supplied
 * `reason` (and any other expected discriminator fields). Narrows the
 * type so the caller can read `err.data.cause.<field>` safely.
 */
export function isTrpcCauseShape<R extends Reason>(
  err: unknown,
  expected: { reason: R },
): err is TrpcErrorWithCause<R> {
  if (typeof err !== "object" || err === null) return false;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return false;
  const cause = (data as { cause?: unknown }).cause;
  if (typeof cause !== "object" || cause === null) return false;
  const reason = (cause as { reason?: unknown }).reason;
  return reason === expected.reason;
}

// ── Zod issue helpers ──────────────────────────────────────────────
//
// A tRPC server-side Zod-validation failure lands on the client as a
// TRPCClientError whose `data.zodIssues` is the raw issue array and
// whose `message` is the *stringified* array (e.g.
// `'[{"code":"invalid_string",…}]'`). Rendering `error.message`
// verbatim leaks JSON to the user — see customers-detail.md AC29 +
// company-settings.md AC28. These helpers pull the readable first-
// issue message off instead.

export interface ZodIssueLike {
  code?: string;
  message?: string;
  path?: readonly (string | number)[];
}

/**
 * Extract the Zod issue array off a caught tRPC error, or `null` when
 * the error didn't carry one. The server's `errorFormatter` in
 * `apps/api/src/trpc.ts` attaches issues as `error.data.zodIssues`.
 */
export function extractZodIssues(err: unknown): readonly ZodIssueLike[] | null {
  if (typeof err !== "object" || err === null) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const issues = (data as { zodIssues?: unknown }).zodIssues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  return issues as readonly ZodIssueLike[];
}

/**
 * Convert one Zod issue into a short, user-facing message. Rewrites
 * Zod's awkward `"String must contain at least 1 character(s)"` to
 * `"Required."`; everything else passes through.
 */
export function friendlyZodMessage(issue: ZodIssueLike): string {
  const msg = issue.message ?? "Invalid value.";
  if (issue.code === "too_small" && /at least 1 character/i.test(msg)) return "Required.";
  return msg;
}

/**
 * Pull the first Zod issue's friendly message off a caught tRPC error.
 * Returns `null` when the error didn't carry a Zod issue array. Use as
 * a display fallback: `firstZodIssueMessage(err) ?? err.message`.
 */
export function firstZodIssueMessage(err: unknown): string | null {
  const issues = extractZodIssues(err);
  if (!issues) return null;
  return friendlyZodMessage(issues[0]!);
}
